/** @file Emits an animation's event keys in [lo, hi) as AnimEvents through core B's event sampler. */
import type { Sample } from '../contracts/sample';
import type { AnimEvent } from '../contracts/state';
import type { AnimationData, SkeletonData } from '../skeletonData';
import { eventHandler } from './timeline/eventHandler';

/**
 * The one events-only Sample every call reuses (R10-02: a step that fires nothing allocates nothing). eventHandler
 * reads and writes `events` and `eventCount` alone, so the other buffers stay empty.
 */
const SINK: Sample = {
  values: new Float64Array(0), mask: new Uint8Array(0), inherit: new Int8Array(0), attachments: new Int32Array(0),
  drawOrder: new Int32Array(0), drawOrderKeyed: false, deform: [], deformKeyed: new Uint8Array(0),
  events: new Int32Array(0), eventCount: 0,
};

/** SINK emptied, with room for `keys` key indices; its buffer is replaced only for a timeline longer than any before. */
function sinkFor(keys: number): Sample {
  if (SINK.events.length < keys) SINK.events = new Int32Array(keys);
  SINK.eventCount = 0;
  return SINK;
}

/**
 * One 'event' AnimEvent per key of anim's event timeline with lo <= time < hi, in key order (several keys on one
 * time all fire, R4-02a). The window is core B's eventHandler (W2-08 R6: one window implementation in core);
 * hi = +Infinity closes a non-looping entry at its end. One handler call per window into the shared sink, which
 * holds every key of the timeline, and the events copied out before the next window (R-2), so no key is ever dropped
 * for capacity. An empty or inverted window (hi <= lo: a remainder that ends a float error below a pass boundary)
 * fires nothing; it never reaches the handler, whose lastTime > time branch would treat it as a loop wrap.
 */
export function fireEventKeys(data: SkeletonData, anim: AnimationData, track: number, lo: number, hi: number, out: AnimEvent[]): void {
  if (!(lo < hi)) return;
  const timeline = anim.eventTimeline < 0 ? undefined : anim.timelines[anim.eventTimeline];
  if (timeline === undefined || timeline.k !== 'event' || timeline.times.length === 0) return;
  const sink = sinkFor(timeline.times.length);
  eventHandler.sample(timeline, hi, lo, sink, data);
  for (let n = 0; n < sink.eventCount; n++) {
    const i = sink.events[n] ?? 0;
    out.push({ type: 'event', track, event: timeline.events[i] ?? -1, time: timeline.times[i] ?? 0, override: timeline.overrides[i] ?? null });
  }
}
